import { ActionReducer, createFeatureSelector, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';
import * as CustomerActions from '../actions/customer.actions';
import { ECompanyBUIncludes, EGlueResource, EUserRoles } from '../configurations/common';
import { IAddress } from '../models/common.models';
import {
  ICompanyBusinessUnitAttributes,
  ICustomerExtended,
  ICustomerInfo,
  ICustomerPreferences,
} from '../models/customer.models';
import { ArrayUtils } from '../utils/array.utils';
import { LocalStorageUtils } from '../utils/localStorage.utils';
import { PayloadUtils } from '../utils/payload.utils';
import { IBusinessRoleAddressResource } from '../models/soldTo-selection.models';
import { IAddressesDataIncluded } from '../models/checkout.models';
import { environment } from '../../environments/environment.defaults';

export interface CustomerState {
  data: any;
  info: ICustomerInfo | null;
  customerAddresses: IAddress[] | undefined;
  updateCustomerAddressesInProgress: boolean;
  shipToAddresses: IAddress[] | null;
  customCustomerShipToAddress: IAddress | null;
  loaded: boolean;
  companyRoles: EUserRoles[];
  error: any;
  companyRolesLoading: boolean;
  store: any;
  consentedTerms: string[] | undefined;
  businessUnits: ICompanyBusinessUnitAttributes[] | undefined;
  businessUnitLoading: boolean;
  businessUnitBlockUsers: boolean;
  businessUnitActive: boolean;
  businessUnitName: string | null;
  businessUnitNumber: string | null;
  customerId: string | null;
  customerData: ICustomerExtended | null;
  customerPreferences: ICustomerPreferences | null;
  soldToAddresses: IAddress[] | null;
  areSoldToAddressesLoaded: boolean;
}

// Initialize State start
export const initialState: CustomerState = {
  data: null,
  info: null,
  customerAddresses: undefined,
  updateCustomerAddressesInProgress: false,
  shipToAddresses: null,
  customCustomerShipToAddress: null,
  loaded: false,
  companyRoles: [EUserRoles.Guest],
  error: null,
  companyRolesLoading: false,
  store: null,
  consentedTerms: undefined,
  businessUnits: undefined,
  businessUnitLoading: false,
  businessUnitActive: true,
  businessUnitBlockUsers: false,
  businessUnitName: null,
  businessUnitNumber: null,
  customerId: null,
  customerData: null,
  customerPreferences: null,
  soldToAddresses: null,
  areSoldToAddressesLoaded: false,
};
// Initialize State end

// Selectors start
export const customerState: MemoizedSelector<object, CustomerState> = createFeatureSelector<CustomerState>('customer');

export const getConsentedTerms: MemoizedSelector<object, string[] | undefined> = createSelector(
  customerState,
  (state: CustomerState) => state.consentedTerms,
);

export const getCompanyRoles: MemoizedSelector<object, EUserRoles[]> = createSelector(
  customerState,
  (state: CustomerState) => state.companyRoles,
);

export const isGuest: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => state.companyRoles.includes(EUserRoles.Guest),
);

export const isViewer: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => state.companyRoles.includes(EUserRoles.Viewer),
);

export const isApprover: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => state.companyRoles.includes(EUserRoles.Approver),
);

export const isBusinessPartnerApprover: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => state.companyRoles.includes(EUserRoles.BusinessPartnerApprover),
);

export const isAdmin: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => state.companyRoles.includes(EUserRoles.Admin),
);

export const isSPCViever: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => state.companyRoles.includes(EUserRoles.SPCViewer),
);

export const isBusinessPartner: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => isCustomerBusinessPartner(state),
);

export const isCustomerLoaded: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => state.loaded,
);

export const accessControlData: MemoizedSelector<object, {
  isGuest: boolean;
  isViewer: boolean;
  isAdmin: boolean;
  isApprover: boolean;
  companyRolesLoading: boolean;
  businessUnitActive: boolean;
  businessUnitBlockUsers: boolean;
  businessUnitName: string;
  loaded: boolean;
}> = createSelector(
  customerState,
  (state: CustomerState) => {
    return {
      isGuest: state.companyRoles.includes(EUserRoles.Guest),
      isViewer: state.companyRoles.includes(EUserRoles.Viewer),
      isAdmin: state.companyRoles.includes(EUserRoles.Admin),
      isApprover: state.companyRoles.includes(EUserRoles.Approver),
      companyRolesLoading: state.companyRolesLoading,
      businessUnitActive: state.businessUnitActive,
      businessUnitBlockUsers: state.businessUnitBlockUsers,
      businessUnitName: state.businessUnitName ?? '',
      loaded: state.loaded,
    };
  },
);

export const selectCustomerDataLoading: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => !state.loaded,
);

export const selectCompanyRolesLoading: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => state.companyRolesLoading,
);

export const getMyOrderPageData: MemoizedSelector<object, {
  companyRoles: EUserRoles[];
  isGuest: boolean;
  id: any;
}> = createSelector(
  customerState,
  (state: CustomerState) => {
    return {
      companyRoles: state.companyRoles,
      isGuest: state.companyRoles.includes(EUserRoles.Guest),
      id: state.data?.id,
    };
  },
);

export const selectUserId: MemoizedSelector<object, any> = createSelector(
  customerState,
  (state: CustomerState) => state.data?.id,
);

export const getMyBusinessUnitName: MemoizedSelector<object, any> = createSelector(
  customerState,
  (state: CustomerState) => state.data?.attributes?.companyBusinessUnit,
);

export const getMyBusinessUnitAndUserRoles: MemoizedSelector<object, {
  customerBusinessUnit: any;
  companyRoles: EUserRoles[];
  customerDataLoaded: boolean;
}> = createSelector(
  customerState,
  (state: CustomerState) => {
    return {
      customerBusinessUnit: state.data?.attributes?.companyBusinessUnit,
      companyRoles: state.companyRoles,
      customerDataLoaded: state.loaded,
    };
  },
);

export const getMyProfileInformation: MemoizedSelector<object, ICustomerInfo | null> = createSelector(
  customerState,
  (state: CustomerState) => state.info,
);

export const selectCompanyUserId: MemoizedSelector<object, string | undefined> = createSelector(
  customerState,
  (state: CustomerState) => state.info?.companyUserUuid,
);

export const isSaturdayShipmentSelected: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => state.info?.pointOfContact?.shipmentMethodName === environment.sparePartSaturdayDeliveryKey,
);

export const getStore: MemoizedSelector<object, any> = createSelector(
  customerState,
  (state: CustomerState) => state.store,
);

export const customerLoadedGuardData: MemoizedSelector<object, {
  loadFailed: boolean;
  isGuest: boolean;
  consentedTerms: string[] | undefined;
}> = createSelector(
  customerState,
  (state: CustomerState) => {
    return {
      loadFailed: !!state.error,
      isGuest: state.companyRoles.includes(EUserRoles.Guest),
      consentedTerms: state.consentedTerms,
    };
  },
);

export const selectBusinessAddress: MemoizedSelector<object, any[] | undefined> = createSelector(
  customerState,
  (state: CustomerState) => {
    return state?.businessUnits ? state.businessUnits.map((data: ICompanyBusinessUnitAttributes) => data.address) : undefined;
  },
);

export const selectBusinessUnits: MemoizedSelector<object, ICompanyBusinessUnitAttributes[] | undefined> = createSelector(
  customerState,
  (state: CustomerState) => {
    return state.businessUnits || undefined;
  },
);

export const selectBusinessUnitLoading: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => {
    return state.businessUnitLoading;
  },
);

export const selectCustomerAddresses: MemoizedSelector<object, IAddress[] | undefined> = createSelector(
  customerState,
  (state: CustomerState) => state.customerAddresses,
);

export const selectUpdateCustomerAddressesInProgress: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  (state: CustomerState) => state.updateCustomerAddressesInProgress,
);

export const selectCustomerShipToAddresses: MemoizedSelector<object, IAddress[] | null> = createSelector(
  customerState,
  (state: CustomerState) => state.shipToAddresses,
);

export const selectCustomerSoldToAddresses: MemoizedSelector<object, IAddress[] | null> = createSelector(
  customerState,
  (state: CustomerState) => state.soldToAddresses,
);

export const selectAreSoldToAddressesLoaded: MemoizedSelector<object, boolean> = createSelector(
  customerState,
  state => state.areSoldToAddressesLoaded,
);

export const selectCustomCustomerShipToAddress: MemoizedSelector<object, IAddress | null> = createSelector(
  customerState,
  (state: CustomerState) => state.customCustomerShipToAddress,
);

export const selectCustomerData: MemoizedSelector<object, {
  customerData: ICustomerExtended | null;
  customerId: string;
  customerPreferences: ICustomerPreferences | null;
  businessUnitName: string;
  businessUnitNumber: string;
  companyRoles: EUserRoles[];
}> = createSelector(
  customerState,
  (state: CustomerState) => {
    return {
      customerData: state.customerData,
      customerId: state.customerId ?? '',
      customerPreferences: state.customerPreferences,
      businessUnitName: state.businessUnitName ?? '',
      businessUnitNumber: state.businessUnitNumber ?? '',
      companyRoles: state.companyRoles,
    };
  },
);

export const selectCustomerPreferences: MemoizedSelector<object, ICustomerPreferences | null> = createSelector(
  customerState,
  (state: CustomerState) => state.customerPreferences,
);
// Selectors end

// Reducer start
export const CustomerReducer: ActionReducer<CustomerState> = createReducer(
  initialState,
  on(CustomerActions.CompanyUsersActionStart, (state) => {
    return {
      ...state,
      companyRolesLoading: true,
    };
  }),
  on(CustomerActions.CompanyUsersActionSuccess, (state, {payload}) => {
    const companyRoles: EUserRoles[] = payload?.included.filter((data: any) => data.type === 'company-roles').map((companyRoles: any) => {
      return companyRoles.attributes.name.toUpperCase();
    });

    const userData: any = payload?.included.find((data: any) => data.type === 'customers');
    const userPreferences: any = payload?.included.find((data: any) => data.type === 'customer-preferences');

    const currentConsentedTerms: string[] = payload?.data[0]?.attributes?.consentedTerms || [];

    const companyBUData: any = payload?.included.find((data: any) => data.type === ECompanyBUIncludes.COMPANY_BUSINESS_UNITS);
    const isBuActive: boolean = companyBUData?.attributes.active || false;
    const buBlockUsers: boolean = companyBUData?.attributes.blockUsers || false;
    const buName: string = companyBUData?.attributes.name || '';
    const buNumber: string = companyBUData?.attributes.businessUnitNumber || '';


    (globalThis as any).selfJSON = {
      gid: userData?.id ?? '',
      country: LocalStorageUtils.getKeyValue('storeId'),
      language: LocalStorageUtils.getKeyValue('lang'),
    };

    return {
      ...state,
      data: payload?.data[0] || null,
      info: userData?.attributes ? {...userData.attributes, companyUserUuid: payload?.data[0]?.id} : null,
      loaded: true,
      companyRoles: companyRoles ?? [EUserRoles.Guest],
      companyRolesLoading: false,
      consentedTerms: currentConsentedTerms,
      businessUnitActive: isBuActive,
      businessUnitBlockUsers: buBlockUsers,
      businessUnitName: buName,
      businessUnitNumber: buNumber,
      customerId: userData?.id || null,
      customerData: userData?.attributes || null,
      customerPreferences: userPreferences?.attributes || null,
    };
  }),
  on(CustomerActions.CompanyUsersActionError, (state, error) => {
    return {
      ...state,
      error,
      companyRoles: [EUserRoles.Guest],
      companyRolesLoading: false,
      loaded: true,
    };
  }),
  on(CustomerActions.updateCustomerPreferences, (state, {data}) => {
    return {
      ...state,
      customerPreferences: data,
    };
  }),
  on(CustomerActions.setStore, (state, {store}) => {
    return {
      ...state,
      store,
    };
  }),
  on(CustomerActions.setPointOfContact, (state, {data}) => {
    return {
      ...state,
      info: state.info ? { ...state.info, pointOfContact: data } : null,
    };
  }),
  on(CustomerActions.clearAttentionTo, (state) => {
    return {
      ...state,
      info: state.info ? {
        ...state.info,
        pointOfContact: {
          ...state.info.pointOfContact,
          attentionTo: undefined,
        },
      } : null,
    };
  }),
  on(CustomerActions.clearPointOfContact, (state) => {
    return {
      ...state,
      info: state.info ? { ...state.info, pointOfContact: undefined } : null,
    };
  }),
  on(CustomerActions.loadCustomerData, (state) => {
    return {
      ...state,
      companyRolesLoading: true,
    };
  }),
  on(CustomerActions.loadCustomerDataError, (state, error) => {
    return {
      ...state,
      error,
      companyRoles: [EUserRoles.Guest],
      companyRolesLoading: false,
      loaded: true,
    };
  }),
  on(CustomerActions.setConsentedTerms, (state, {newConsentedTerms}) => {
    return {
      ...state,
      consentedTerms: newConsentedTerms,
    };
  }),
  on(CustomerActions.startGetBusinessUnitAction, (state) => {
    return {
      ...state,
      businessUnitLoading: true,
    };
  }),
  on(CustomerActions.successGetBusinessUnitAction, (state: CustomerState, {payload}) => {
    const businessUnits: any[] = [];
    payload.data.forEach((unit: any) => {
      const company: any[] = PayloadUtils.getIncludedData(unit, payload.included, 'companies');
      const address: any[] = PayloadUtils.getIncludedData(unit, payload.included, 'company-business-unit-addresses');
      businessUnits.push({
        ...unit.attributes,
        institutionName: company && company.length > 0 ? company[0].attributes.name : '',
        address: address && address.length > 0 ? {id: address[0].id, ...address[0].attributes} : undefined,
      });
    });
    return {
      ...state,
      businessUnits,
      businessUnitLoading: false,
      loaded: true,
    };
  }),
  on(CustomerActions.errorGetBusinessUnitAction, (state, {error}) => {
    return {
      ...state,
      error,
      businessUnits: undefined,
      businessUnitLoading: false,
      loaded: true,
    };
  }),
  on(CustomerActions.successGetCustomerAddressAction, (state: CustomerState, {payload}) => {
    const addresses: IAddress[] = payload.data.map((address: any) => {
      return convertCustomerAddressToAddress(address);
    });
    return {
      ...state,
      customerAddresses: addresses,
    };
  }),
  on(CustomerActions.beginAddCustomerAddressAction, (state: CustomerState) => {
    return {
      ...state,
      updateCustomerAddressesInProgress: true,
    };
  }),
  on(CustomerActions.successAddCustomerAddressAction, (state: CustomerState, {data}) => {
    const addresses: IAddress[] = [...(state.customerAddresses || [])];
    addresses.push(convertCustomerAddressToAddress(data?.data));

    return {
      ...state,
      customerAddresses: addresses,
      updateCustomerAddressesInProgress: false,
    };
  }),
  on(CustomerActions.errorAddCustomerAddressAction, (state: CustomerState, {error}) => {
    return {
      ...state,
      error,
      updateCustomerAddressesInProgress: false,
    };
  }),
  on(CustomerActions.beginUpdateCustomerAddressAction, (state: CustomerState) => {
    return {
      ...state,
      updateCustomerAddressesInProgress: true,
    };
  }),
  on(CustomerActions.successUpdateCustomerAddressAction, (state: CustomerState, {data}) => {
    const addressIndex: number = state.customerAddresses?.findIndex((address: IAddress) => address?.id === data?.data?.id) ?? -1;
    const addresses: IAddress[] = [...(state.customerAddresses || [])];

    if (addressIndex !== -1) {
      addresses[addressIndex] = convertCustomerAddressToAddress(data?.data);
    }

    return {
      ...state,
      customerAddresses: addresses,
      updateCustomerAddressesInProgress: false,
    };
  }),
  on(CustomerActions.errorUpdateCustomerAddressAction, (state: CustomerState, {error}) => {
    return {
      ...state,
      error,
      updateCustomerAddressesInProgress: false,
    };
  }),
  on(CustomerActions.beginDeleteCustomerAddressAction, (state: CustomerState) => {
    return {
      ...state,
      updateCustomerAddressesInProgress: true,
    };
  }),
  on(CustomerActions.successDeleteCustomerAddressAction, (state: CustomerState, {addressId}) => {
    return {
      ...state,
      customerAddresses: state.customerAddresses?.filter(address => address?.id !== addressId),
      updateCustomerAddressesInProgress: false,
    };
  }),
  on(CustomerActions.errorDeleteCustomerAddressAction, (state: CustomerState, {error}) => {
    return {
      ...state,
      error,
      updateCustomerAddressesInProgress: false,
    };
  }),
  on(CustomerActions.successDeletedPreferredShipToAddressRemovalAction, (state: CustomerState, {addressId, data}) => {
    return {
      ...state,
      customerAddresses: state.customerAddresses?.filter(address => address?.id !== addressId),
      updateCustomerAddressesInProgress: false,
      customerPreferences: data,
    };
  }),
  on(CustomerActions.errorDeletedPreferredShipToAddressRemovalAction, (state: CustomerState, {error}) => {
    return {
      ...state,
      error,
      updateCustomerAddressesInProgress: false,
    };
  }),
  on(CustomerActions.loadCustomerShipToAddressesSuccess, (state: CustomerState, {payload}) => {
    if (isCustomerBusinessPartner(state)) {
      return {
        ...state,
      };
    } else {
      const allAddresses: any[] = payload.data[0].attributes?.addresses;
      const addressesWithNames: any[] = allAddresses.filter((address: any) => address.name !== null);
      const uniqueAddresses: IAddress[] = ArrayUtils.uniqueObjects(addressesWithNames, 'sapId');
      return {
        ...state,
        shipToAddresses: uniqueAddresses,
      };
    }
  }),
  on(CustomerActions.loadCustomerSoldToAddressesSuccess, (state: CustomerState, {payload}) => {
    const soldToAddresses: IAddress[] = payload.data
      .map((soldToAddress: any) => convertBusinessRoleAddressToAddress(soldToAddress));
    const included: any[] = payload.included ?? [];
    const shipToAddresses: IAddress[] = included
      .filter((includedAddress: any) => includedAddress.type === EGlueResource.SHIP_TO_ADDRESSES)
      .map((shipToAddress: any) => convertBusinessRoleAddressToAddress(shipToAddress));
    return {
      ...state,
      soldToAddresses,
      shipToAddresses,
      areSoldToAddressesLoaded: true
    };
  }),
  on(CustomerActions.setCustomShipToAddress, (state: CustomerState, {data}) => {
    return {
      ...state,
      customCustomerShipToAddress: data,
    };
  }),
  on(CustomerActions.clearCustomShipToAddress, (state) => {
    return {
      ...state,
      customCustomerShipToAddress: null,
    };
  }),

  on((CustomerActions.clearCompanyRoles), (state) => {
    return {
      ...state,
      companyRoles: [EUserRoles.Guest],
    };
  }),
);

// Reducer end

function convertCustomerAddressToAddress(address: IAddressesDataIncluded): IAddress {
  return {
    ...address?.attributes,
    id: address?.id,
    name: address?.attributes?.firstName,
    state: address?.attributes?.address2,
    isCustom: true,
  };
}

function convertBusinessRoleAddressToAddress(address: IBusinessRoleAddressResource<any>): IAddress {
  return {
    ...address.attributes,
    // backfill some missing fields from the BE response
    salutation: '',
    firstName: '',
    lastName: '',
    company: address.attributes.name,
    phone: '',
    isDefaultBilling: false,
    isDefaultShipping: false,
  };
}

function isCustomerBusinessPartner(state: CustomerState): boolean {
  return state.companyRoles.includes(EUserRoles.BusinessPartnerApprover)
    || state.companyRoles.includes(EUserRoles.BusinessPartnerBuyer);
}
